import React, { useEffect, useState } from 'react';
import { reportsService } from './reportsService';
import {
  ReportDefinition,
  ReportRun,
  ReportSnapshot,
  ReportExport,
  ReportSchedule,
  RegulatorySubmission,
  RegulatoryReportDefinition,
  ReportTemplate,
  ReportDashboardKpis,
} from './types';
import { ReportsMetricsCards } from './components/ReportsMetricsCards';
import { ReportCreateModal } from './components/ReportCreateModal';
import { X } from 'lucide-react';
import { TablePagination, useTablePagination } from '../../shared/TablePagination';
import { useTableSort, SortableTh } from '../../shared/useTableSort';

export const ReportsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<
    'centre' | 'runs' | 'snapshots' | 'exports' | 'schedules' | 'regulatory' | 'templates'
  >('centre');
  const [loading, setLoading] = useState(true);

  // States
  const [kpis, setKpis] = useState<ReportDashboardKpis | null>(null);
  const [definitions, setDefinitions] = useState<ReportDefinition[]>([]);
  const [runs, setRuns] = useState<ReportRun[]>([]);
  const [snapshots, setSnapshots] = useState<ReportSnapshot[]>([]);
  const [exportsList, setExportsList] = useState<ReportExport[]>([]);
  const [schedules, setSchedules] = useState<ReportSchedule[]>([]);
  const [regDefinitions, setRegDefinitions] = useState<RegulatoryReportDefinition[]>([]);
  const [regSubmissions, setRegSubmissions] = useState<RegulatorySubmission[]>([]);
  const [templates, setTemplates] = useState<ReportTemplate[]>([]);

  // Filters & Modals
  const [filterFamily, setFilterFamily] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [selectedSnapshot, setSelectedSnapshot] = useState<ReportSnapshot | null>(null);

  useEffect(() => {
    loadAll();
  }, []);

  const loadAll = async () => {
    try {
      setLoading(true);
      const [
        kpiData,
        defData,
        runData,
        snapData,
        expData,
        schData,
        regDefData,
        regSubData,
        tmplData,
      ] = await Promise.all([
        reportsService.getDashboardKpis(),
        reportsService.getDefinitions(),
        reportsService.getRuns(),
        reportsService.getSnapshots(),
        reportsService.getExports(),
        reportsService.getSchedules(),
        reportsService.getRegulatoryDefinitions(),
        reportsService.getRegulatorySubmissions(),
        reportsService.getTemplates(),
      ]);
      setKpis(kpiData);
      setDefinitions(defData);
      setRuns(runData);
      setSnapshots(snapData);
      setExportsList(expData);
      setSchedules(schData);
      setRegDefinitions(regDefData);
      setRegSubmissions(regSubData);
      setTemplates(tmplData);
    } catch (err) {
      console.error('Error loading reports data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateDefinition = async (data: any) => {
    await reportsService.createDefinition(data);
    await loadAll();
  };

  const handleExecuteRun = async (reportId: string) => {
    try {
      await reportsService.executeRun(reportId, {
        executed_by: 'Safety Director (Central Ops)',
      });
      await loadAll();
      alert('Report executed successfully and immutable snapshot generated.');
    } catch (err: any) {
      alert(`Error: ${err.message}`);
    }
  };

  const handleSubmitRegulatory = async (id: string) => {
    try {
      await reportsService.submitToAuthority(id, {
        approved_by: 'Accountable Manager',
        external_reference: `EASA-ACK-${Date.now().toString().slice(-6)}`,
      });
      await loadAll();
      alert('Report transmitted successfully to authority portal.');
    } catch (err: any) {
      alert(`Error: ${err.message}`);
    }
  };

  const filteredDefinitions = definitions.filter((d) => {
    if (filterFamily && d.family !== filterFamily) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return d.report_code.toLowerCase().includes(q) || d.title.toLowerCase().includes(q);
    }
    return true;
  });

  const { sortedItems: sortedDefinitions, sortField: defSortField, sortDirection: defSortDir, toggleSort: toggleDefSort } = useTableSort(filteredDefinitions);
  const { sortedItems: sortedRuns, sortField: runSortField, sortDirection: runSortDir, toggleSort: toggleRunSort } = useTableSort(runs);
  const { sortedItems: sortedSnapshots, sortField: snapSortField, sortDirection: snapSortDir, toggleSort: toggleSnapSort } = useTableSort(snapshots);
  const { sortedItems: sortedExports, sortField: expSortField, sortDirection: expSortDir, toggleSort: toggleExpSort } = useTableSort(exportsList);
  const { sortedItems: sortedSchedules, sortField: schSortField, sortDirection: schSortDir, toggleSort: toggleSchSort } = useTableSort(schedules);
  const { sortedItems: sortedRegSubs, sortField: regSortField, sortDirection: regSortDir, toggleSort: toggleRegSort } = useTableSort(regSubmissions);
  const { sortedItems: sortedTemplates, sortField: tmplSortField, sortDirection: tmplSortDir, toggleSort: toggleTmplSort } = useTableSort(templates);

  const {
    currentPage: reportPage,
    setCurrentPage: setReportPage,
    pageSize: reportPageSize,
    setPageSize: setReportPageSize,
    totalItems: totalReportItems,
    paginatedItems: paginatedDefinitions,
  } = useTablePagination(sortedDefinitions, 25);

  return (
    <div>
      {/* 1. Hero Banner */}
      <div className="page-header-banner">
        <div className="badge">AUDITS & REGULATORY FILING</div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '16px' }}>
          <div>
            <h1>Reports & Regulatory Filing</h1>
            <p>
              Report definitions, automated runs, immutable cryptographic snapshots, and authority submissions (EASA/SHGM).
            </p>
          </div>
          <button
            onClick={() => setIsCreateModalOpen(true)}
            className="btn-primary"
            style={{ width: 'auto', padding: '10px 18px' }}
          >
            + New Report Definition
          </button>
        </div>
      </div>

      {/* 2. KPI Cards */}
      <ReportsMetricsCards kpis={kpis} />

      {/* 3. Navigation Tabs */}
      <div className="nav-tabs">
        {[
          { key: 'centre', label: '📑 Report Catalogue' },
          { key: 'runs', label: '⏱️ Execution Runs' },
          { key: 'snapshots', label: '🔒 Immutable Snapshots' },
          { key: 'exports', label: '📁 Exported Files' },
          { key: 'schedules', label: '📅 Scheduled Dispatches' },
          { key: 'regulatory', label: '🏛️ Regulatory Submissions' },
          { key: 'templates', label: '📐 Report Templates' },
        ].map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key as any)}
            className={`tab-btn ${activeTab === tab.key ? 'active' : ''}`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* TAB 1: Report Centre Catalog */}
      {activeTab === 'centre' && (
        <div>
          {/* Filters Bar */}
          <div
            style={{
              display: 'flex',
              gap: '12px',
              marginBottom: '16px',
              background: '#ffffff',
              padding: '12px 16px',
              borderRadius: '8px',
              border: '1px solid #e2e8f0',
            }}
          >
            <input
              type="text"
              placeholder="Search by code or title..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                flex: 1,
                padding: '8px 12px',
                borderRadius: '6px',
                border: '1px solid #cbd5e1',
                fontSize: '13px',
              }}
            />
            <select
              value={filterFamily}
              onChange={(e) => setFilterFamily(e.target.value)}
              style={{
                padding: '8px 12px',
                borderRadius: '6px',
                border: '1px solid #cbd5e1',
                fontSize: '13px',
              }}
            >
              <option value="">All Families</option>
              <option value="SAFETY">SAFETY</option>
              <option value="AUDIT">AUDIT</option>
              <option value="MANAGEMENT">MANAGEMENT</option>
              <option value="REGULATORY">REGULATORY</option>
              <option value="RISK">RISK</option>
            </select>
          </div>

          {/* Catalog Table */}
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <SortableTh field="report_code" currentField={defSortField} currentDirection={defSortDir} onSort={toggleDefSort}>
                    Report Code
                  </SortableTh>
                  <SortableTh field="title" currentField={defSortField} currentDirection={defSortDir} onSort={toggleDefSort}>
                    Title & Scope
                  </SortableTh>
                  <SortableTh field="family" currentField={defSortField} currentDirection={defSortDir} onSort={toggleDefSort}>
                    Family
                  </SortableTh>
                  <SortableTh field="current_version" currentField={defSortField} currentDirection={defSortDir} onSort={toggleDefSort}>
                    Version
                  </SortableTh>
                  <SortableTh field="owner_name" currentField={defSortField} currentDirection={defSortDir} onSort={toggleDefSort}>
                    Owner
                  </SortableTh>
                  <SortableTh field="status" currentField={defSortField} currentDirection={defSortDir} onSort={toggleDefSort}>
                    Status
                  </SortableTh>
                  <th style={{ textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedDefinitions.map((d) => (
                  <tr key={d.id}>
                    <td style={{ fontFamily: 'monospace', fontWeight: 600, color: '#0f172a' }}>{d.report_code}</td>
                    <td>
                      <div style={{ fontWeight: 600, color: '#0f172a' }}>{d.title}</div>
                      <div style={{ fontSize: '12px', color: '#64748b' }}>{d.description}</div>
                    </td>
                    <td>
                      <span className="badge-pill badge-neutral">{d.family}</span>
                    </td>
                    <td style={{ fontWeight: 600, color: '#2563eb' }}>v{d.current_version}</td>
                    <td style={{ color: '#64748b' }}>{d.owner_name}</td>
                    <td>
                      <span className="badge-pill badge-success">{d.status}</span>
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <button
                        onClick={() => handleExecuteRun(d.id)}
                        className="btn-primary"
                        style={{ width: 'auto', padding: '4px 12px', fontSize: '12px' }}
                      >
                        ⚡ Run Now
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <TablePagination
            currentPage={reportPage}
            totalItems={totalReportItems}
            pageSize={reportPageSize}
            onPageChange={setReportPage}
            onPageSizeChange={setReportPageSize}
            itemLabel="reports"
          />
        </div>
      )}

      {/* TAB 2: Execution Runs */}
      {activeTab === 'runs' && (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <SortableTh field="id" currentField={runSortField} currentDirection={runSortDir} onSort={toggleRunSort}>
                  Run Identifier
                </SortableTh>
                <SortableTh field="report_title" currentField={runSortField} currentDirection={runSortDir} onSort={toggleRunSort}>
                  Target Report
                </SortableTh>
                <SortableTh field="execution_type" currentField={runSortField} currentDirection={runSortDir} onSort={toggleRunSort}>
                  Execution Mode
                </SortableTh>
                <SortableTh field="executed_by" currentField={runSortField} currentDirection={runSortDir} onSort={toggleRunSort}>
                  Executed By
                </SortableTh>
                <SortableTh field="records_count" currentField={runSortField} currentDirection={runSortDir} onSort={toggleRunSort}>
                  Records Count
                </SortableTh>
                <SortableTh field="duration_ms" currentField={runSortField} currentDirection={runSortDir} onSort={toggleRunSort}>
                  Duration
                </SortableTh>
                <SortableTh field="created_at" currentField={runSortField} currentDirection={runSortDir} onSort={toggleRunSort}>
                  Date
                </SortableTh>
                <SortableTh field="status" currentField={runSortField} currentDirection={runSortDir} onSort={toggleRunSort}>
                  Status
                </SortableTh>
              </tr>
            </thead>
            <tbody>
              {sortedRuns.map((r) => (
                <tr key={r.id}>
                  <td style={{ fontFamily: 'monospace', fontWeight: 600, color: '#0f172a' }}>{r.id}</td>
                  <td>
                    <div style={{ fontWeight: 600 }}>{r.report_title || r.report_code}</div>
                    <div style={{ fontSize: '11px', color: '#64748b' }}>v{r.version_number}</div>
                  </td>
                  <td>
                    <span className="badge-pill badge-info">{r.execution_type}</span>
                  </td>
                  <td style={{ color: '#475569' }}>{r.executed_by}</td>
                  <td style={{ color: '#475569' }}>{r.records_count} records</td>
                  <td style={{ color: '#64748b' }}>{r.duration_ms} ms</td>
                  <td style={{ color: '#64748b' }}>{r.created_at.slice(0, 16).replace('T', ' ')}</td>
                  <td>
                    <span className="badge-pill badge-success">{r.status}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* TAB 3: Snapshots */}
      {activeTab === 'snapshots' && (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <SortableTh field="id" currentField={snapSortField} currentDirection={snapSortDir} onSort={toggleSnapSort}>
                  Snapshot ID
                </SortableTh>
                <SortableTh field="report_title" currentField={snapSortField} currentDirection={snapSortDir} onSort={toggleSnapSort}>
                  Report
                </SortableTh>
                <SortableTh field="snapshot_type" currentField={snapSortField} currentDirection={snapSortDir} onSort={toggleSnapSort}>
                  Type
                </SortableTh>
                <SortableTh field="data_hash" currentField={snapSortField} currentDirection={snapSortDir} onSort={toggleSnapSort}>
                  SHA-256 Digest
                </SortableTh>
                <SortableTh field="generated_at" currentField={snapSortField} currentDirection={snapSortDir} onSort={toggleSnapSort}>
                  Generated At
                </SortableTh>
                <th style={{ textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedSnapshots.map((s) => (
                <tr key={s.id}>
                  <td style={{ fontFamily: 'monospace', fontWeight: 600, color: '#0f172a' }}>{s.id}</td>
                  <td style={{ fontWeight: 600 }}>{s.report_title || s.report_run_id}</td>
                  <td>
                    <span className="badge-pill badge-info">{s.snapshot_type}</span>
                  </td>
                  <td style={{ fontFamily: 'monospace', fontSize: '11px', color: '#64748b' }}>
                    {s.data_hash.slice(0, 16)}...{s.data_hash.slice(-8)}
                  </td>
                  <td style={{ color: '#64748b' }}>{s.generated_at.slice(0, 16).replace('T', ' ')}</td>
                  <td style={{ textAlign: 'right' }}>
                    <button
                      onClick={() => setSelectedSnapshot(s)}
                      className="btn-secondary"
                      style={{ padding: '4px 10px', fontSize: '12px' }}
                    >
                      🔍 Inspect Payload
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* TAB 4: Exports */}
      {activeTab === 'exports' && (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <SortableTh field="file_name" currentField={expSortField} currentDirection={expSortDir} onSort={toggleExpSort}>
                  File Name
                </SortableTh>
                <SortableTh field="format" currentField={expSortField} currentDirection={expSortDir} onSort={toggleExpSort}>
                  Format
                </SortableTh>
                <SortableTh field="file_size_bytes" currentField={expSortField} currentDirection={expSortDir} onSort={toggleExpSort}>
                  Size
                </SortableTh>
                <SortableTh field="storage_key" currentField={expSortField} currentDirection={expSortDir} onSort={toggleExpSort}>
                  Storage Key
                </SortableTh>
                <SortableTh field="generated_at" currentField={expSortField} currentDirection={expSortDir} onSort={toggleExpSort}>
                  Date
                </SortableTh>
                <SortableTh field="status" currentField={expSortField} currentDirection={expSortDir} onSort={toggleExpSort}>
                  Status
                </SortableTh>
              </tr>
            </thead>
            <tbody>
              {sortedExports.map((exp) => (
                <tr key={exp.id}>
                  <td style={{ fontWeight: 600, color: '#0f172a' }}>📄 {exp.file_name}</td>
                  <td>
                    <span className="badge-pill badge-info">{exp.format}</span>
                  </td>
                  <td style={{ color: '#64748b' }}>{(exp.file_size_bytes / 1024).toFixed(1)} KB</td>
                  <td style={{ color: '#64748b', fontSize: '12px', fontFamily: 'monospace' }}>{exp.storage_key}</td>
                  <td style={{ color: '#64748b' }}>{exp.generated_at.slice(0, 16).replace('T', ' ')}</td>
                  <td>
                    <span className="badge-pill badge-success">{exp.status}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* TAB 5: Schedules */}
      {activeTab === 'schedules' && (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <SortableTh field="name" currentField={schSortField} currentDirection={schSortDir} onSort={toggleSchSort}>
                  Schedule Name
                </SortableTh>
                <SortableTh field="report_title" currentField={schSortField} currentDirection={schSortDir} onSort={toggleSchSort}>
                  Target Report
                </SortableTh>
                <SortableTh field="recurrence_rule" currentField={schSortField} currentDirection={schSortDir} onSort={toggleSchSort}>
                  Recurrence Rule
                </SortableTh>
                <SortableTh field="delivery_method" currentField={schSortField} currentDirection={schSortDir} onSort={toggleSchSort}>
                  Delivery Method
                </SortableTh>
                <SortableTh field="recipients_json" currentField={schSortField} currentDirection={schSortDir} onSort={toggleSchSort}>
                  Recipients
                </SortableTh>
                <SortableTh field="status" currentField={schSortField} currentDirection={schSortDir} onSort={toggleSchSort}>
                  Status
                </SortableTh>
              </tr>
            </thead>
            <tbody>
              {sortedSchedules.map((s) => (
                <tr key={s.id}>
                  <td style={{ fontWeight: 600, color: '#0f172a' }}>{s.name}</td>
                  <td>{s.report_title}</td>
                  <td style={{ fontWeight: 600, color: '#2563eb' }}>{s.recurrence_rule}</td>
                  <td style={{ color: '#64748b' }}>{s.execution_format} • {s.delivery_method}</td>
                  <td style={{ color: '#475569', fontSize: '12px' }}>{s.recipients_json}</td>
                  <td>
                    <span className="badge-pill badge-success">{s.status}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* TAB 6: Regulatory Submissions */}
      {activeTab === 'regulatory' && (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <SortableTh field="submission_number" currentField={regSortField} currentDirection={regSortDir} onSort={toggleRegSort}>
                  Submission ID
                </SortableTh>
                <SortableTh field="report_title" currentField={regSortField} currentDirection={regSortDir} onSort={toggleRegSort}>
                  Authority & Report
                </SortableTh>
                <SortableTh field="prepared_by" currentField={regSortField} currentDirection={regSortDir} onSort={toggleRegSort}>
                  Prepared By
                </SortableTh>
                <SortableTh field="approved_by" currentField={regSortField} currentDirection={regSortDir} onSort={toggleRegSort}>
                  Approved By
                </SortableTh>
                <SortableTh field="external_reference" currentField={regSortField} currentDirection={regSortDir} onSort={toggleRegSort}>
                  External Ref
                </SortableTh>
                <SortableTh field="submission_status" currentField={regSortField} currentDirection={regSortDir} onSort={toggleRegSort}>
                  Status
                </SortableTh>
                <th style={{ textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedRegSubs.map((sub) => (
                <tr key={sub.id}>
                  <td style={{ fontFamily: 'monospace', fontWeight: 600, color: '#0f172a' }}>{sub.submission_number}</td>
                  <td>
                    <div style={{ fontWeight: 600 }}>{sub.authority_name} — {sub.report_title}</div>
                    <div style={{ fontSize: '11px', color: '#64748b' }}>
                      Period: {sub.reporting_period_start || '-'} → {sub.reporting_period_end || '-'}
                    </div>
                  </td>
                  <td style={{ color: '#475569' }}>{sub.prepared_by}</td>
                  <td style={{ color: '#475569' }}>{sub.approved_by || '-'}</td>
                  <td style={{ color: '#2563eb', fontSize: '12px' }}>{sub.external_reference || '-'}</td>
                  <td>
                    <span className={`badge-pill ${sub.submission_status === 'SUBMITTED' ? 'badge-success' : 'badge-warning'}`}>
                      {sub.submission_status}
                    </span>
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    {sub.submission_status === 'DRAFT' && (
                      <button
                        onClick={() => handleSubmitRegulatory(sub.id)}
                        className="btn-primary"
                        style={{ width: 'auto', padding: '4px 10px', fontSize: '12px', background: '#10b981' }}
                      >
                        📤 Submit to Authority
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* TAB 7: Templates */}
      {activeTab === 'templates' && (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <SortableTh field="template_code" currentField={tmplSortField} currentDirection={tmplSortDir} onSort={toggleTmplSort}>
                  Template Code
                </SortableTh>
                <SortableTh field="name" currentField={tmplSortField} currentDirection={tmplSortDir} onSort={toggleTmplSort}>
                  Template Title & Description
                </SortableTh>
                <SortableTh field="template_type" currentField={tmplSortField} currentDirection={tmplSortDir} onSort={toggleTmplSort}>
                  Type
                </SortableTh>
                <SortableTh field="status" currentField={tmplSortField} currentDirection={tmplSortDir} onSort={toggleTmplSort}>
                  Status
                </SortableTh>
              </tr>
            </thead>
            <tbody>
              {sortedTemplates.map((t) => (
                <tr key={t.id}>
                  <td style={{ fontFamily: 'monospace', fontWeight: 600, color: '#0f172a' }}>{t.template_code}</td>
                  <td>
                    <div style={{ fontWeight: 600 }}>{t.name}</div>
                    <div style={{ fontSize: '11px', color: '#64748b' }}>{t.description}</div>
                  </td>
                  <td>
                    <span className="badge-pill badge-info">{t.template_type}</span>
                  </td>
                  <td>
                    <span className="badge-pill badge-success">{t.status}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Snapshot Content Viewer Modal */}
      {selectedSnapshot && (
        <div className="modal-backdrop">
          <div className="modal-card">
            <div className="modal-header">
              <div className="modal-header-content">
                <h3>🔒 Immutable Snapshot Payload</h3>
                <p>Verifiable cryptographically sealed report snapshot</p>
              </div>
              <button
                type="button"
                onClick={() => setSelectedSnapshot(null)}
                className="modal-close-btn"
                aria-label="Close modal"
              >
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '12px' }}>
                <strong>SHA-256 Digest:</strong> <code>{selectedSnapshot.data_hash}</code>
              </div>
              <pre
                style={{
                  background: '#f8fafc',
                  padding: '16px',
                  borderRadius: '6px',
                  border: '1px solid #e2e8f0',
                  fontSize: '12px',
                  maxHeight: '350px',
                  overflowY: 'auto',
                }}
              >
                {JSON.stringify(JSON.parse(selectedSnapshot.rendered_content), null, 2)}
              </pre>
            </div>
            <div className="modal-footer">
              <button onClick={() => setSelectedSnapshot(null)} className="btn-secondary">
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Modal */}
      {isCreateModalOpen && (
        <ReportCreateModal
          onClose={() => setIsCreateModalOpen(false)}
          onSubmit={handleCreateDefinition}
        />
      )}
    </div>
  );
};

export default ReportsPage;
